import anthropicIcon from 'assets/images/ai-providers/anthropic.svg';
import azureIcon from 'assets/images/ai-providers/azure-color.svg';
import customIcon from 'assets/images/ai-providers/custom.svg';
import deepseekIcon from 'assets/images/ai-providers/deepseek-color.svg';
import geminiIcon from 'assets/images/ai-providers/gemini-color.svg';
import mistralIcon from 'assets/images/ai-providers/mistral-color.svg';
import openaiIcon from 'assets/images/ai-providers/openai.svg';

import { IProvider, ServicePlatform } from './models.types';

export const createCustomProvider = (): IProvider => ({
  Provider: 'CUSTOM',
  Url: '',
  DocLink: '',
  Description:
    'Bring your own AI model to the platform by connecting external APIs for full customization and control.',
  Order: 999999,
});

export const ProviderToPlatformMap: Record<string, ServicePlatform> = {
  openai: ServicePlatform.OFFICIAL_API,
  anthropic: ServicePlatform.OFFICIAL_API,
  google: ServicePlatform.OFFICIAL_API,
  gemini: ServicePlatform.OFFICIAL_API,
  mistral: ServicePlatform.OFFICIAL_API,
  deepseek: ServicePlatform.OFFICIAL_API,
  azure: ServicePlatform.OPEN_DEPLOYMENT,
  openrouter: ServicePlatform.OPEN_DEPLOYMENT,
  custom: ServicePlatform.OPEN_DEPLOYMENT,
};

export const ProviderNameMap: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Gemini',
  gemini: 'Gemini',
  mistral: 'Mistral',
  deepseek: 'DeepSeek',
  azure: 'Azure',
  openrouter: 'OpenRouter',
  custom: 'Custom Model',
};

const ProviderDescriptionMap: Record<string, string> = {
  openai: 'A large language model service provided by OpenAI.',
  anthropic:
    'Claude is a family of large language models released by Anthropic, featuring strong reasoning and safety.',
  google:
    'Gemini is a generative AI model developed by Google DeepMind, designed for multimodal tasks.',
  gemini:
    'Gemini is a generative AI model developed by Google DeepMind, designed for multimodal tasks.',
  mistral:
    'Based in Europe, Mistral AI focuses on building secure, robust, and general-purpose foundation models.',
  deepseek:
    'DeepSeek is dedicated to the development of high-performance, low-cost AI models.',
  azure:
    'Connect Azure-hosted model deployments for enterprise-managed inference and infrastructure control.',
  openrouter:
    'Route requests across multiple third-party models through OpenRouter with one integration.',
  custom:
    'Bring your own AI model to the platform by connecting external APIs for full customization and control.',
};

export const ProviderIconMap: Record<string, string | undefined> = {
  openai: openaiIcon,
  anthropic: anthropicIcon,
  google: geminiIcon,
  gemini: geminiIcon,
  mistral: mistralIcon,
  deepseek: deepseekIcon,
  azure: azureIcon,
  custom: customIcon,
};

const MonochromeProviderIcons = new Set(['openai', 'anthropic', 'custom']);

export const getProviderDisplayName = (provider?: string) => {
  if (!provider) return 'Provider';
  return ProviderNameMap[provider.toLowerCase()] ?? provider;
};

export const getProviderIcon = (provider?: string) => {
  if (!provider) return undefined;
  return ProviderIconMap[provider.toLowerCase()];
};

export const getProviderIconClassName = (provider?: string, theme?: string) => {
  if (!provider) return 'h-6 w-6 object-contain';

  const providerKey = provider.toLowerCase();
  const isMonochrome = MonochromeProviderIcons.has(providerKey);

  return [
    'h-6 w-6 object-contain',
    theme === 'dark' && isMonochrome ? 'brightness-0 invert' : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const getProviderDescription = (provider?: string) => {
  if (!provider) return '';
  return ProviderDescriptionMap[provider.toLowerCase()] ?? '';
};

export const createFallbackProviders = (): IProvider[] => [
  {
    Provider: 'openai',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('openai'),
    Order: 1,
  },
  {
    Provider: 'anthropic',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('anthropic'),
    Order: 2,
  },
  {
    Provider: 'google',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('google'),
    Order: 3,
  },
  {
    Provider: 'deepseek',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('deepseek'),
    Order: 4,
  },
  {
    Provider: 'mistral',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('mistral'),
    Order: 5,
  },
  {
    Provider: 'azure',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('azure'),
    Order: 6,
  },
  {
    Provider: 'openrouter',
    Url: '',
    DocLink: '',
    Description: getProviderDescription('openrouter'),
    Order: 7,
  },
];

export const sortProviders = (providers: IProvider[]) =>
  [...providers].sort((a, b) => Number(a.Order) - Number(b.Order));

export const mergeProviders = (providers: IProvider[]) => {
  const providerMap = new Map<string, IProvider>();

  providers.forEach((provider) => {
    const key = provider.Provider.toLowerCase();
    if (!providerMap.has(key)) {
      providerMap.set(key, provider);
    }
  });

  return Array.from(providerMap.values());
};
